type StorageProvider = () => Storage | null

export type SafeStorage = {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => boolean
  removeItem: (key: string) => boolean
  getJSON: <T>(key: string, fallback: T) => T
  setJSON: (key: string, value: unknown) => boolean
}

function createSafeStorage(getStorage: StorageProvider): SafeStorage {
  const getItem = (key: string): string | null => {
    try {
      return getStorage()?.getItem(key) ?? null
    } catch {
      return null
    }
  }

  const setItem = (key: string, value: string): boolean => {
    try {
      const storage = getStorage()
      if (!storage) return false
      storage.setItem(key, value)
      return true
    } catch {
      return false
    }
  }

  const removeItem = (key: string): boolean => {
    try {
      const storage = getStorage()
      if (!storage) return false
      storage.removeItem(key)
      return true
    } catch {
      return false
    }
  }

  const getJSON = <T>(key: string, fallback: T): T => {
    const raw = getItem(key)
    if (raw === null) return fallback
    try {
      return JSON.parse(raw) as T
    } catch {
      return fallback
    }
  }

  const setJSON = (key: string, value: unknown): boolean => {
    try {
      return setItem(key, JSON.stringify(value))
    } catch {
      return false
    }
  }

  return { getItem, setItem, removeItem, getJSON, setJSON }
}

export const safeLocalStorage = createSafeStorage(() => {
  if (typeof window === 'undefined') return null
  return window.localStorage
})

export const safeSessionStorage = createSafeStorage(() => {
  if (typeof window === 'undefined') return null
  return window.sessionStorage
})
